import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Ellipsis, type LayoutDashboard } from 'lucide-react';
import { Modal } from '@/components/ui/modal';
import { useCapabilities, useIsReadOnly } from '@/lib/auth/auth-context';
import { cn } from '@/lib/cn';

/**
 * One module's navigation bar.
 *
 * Split out of `AppShell` when Overseer became two applications behind one
 * sign-in. The shell keeps the page chrome — skip link, theme, language,
 * sign-out, the scroll container — and this renders whichever module the user
 * is in. A module supplies a `ModuleNav`; nothing else about it is special.
 */

export type NavItem = {
  /** A declared route pattern, not a concrete URL — typed routing requires it. */
  to: string;
  /**
   * An i18n key.
   *
   * Deliberately a plain `string` rather than a union of keys owned by the
   * shell: a fixed union would keep a module from adding a nav item without
   * editing the shell. `pnpm i18n:check` verifies every `t()` literal resolves,
   * so a typo is still caught, just by the check that already does it rather
   * than by a type here.
   */
  labelKey: string;
  icon: typeof LayoutDashboard;
  params?: Record<string, string>;
  /**
   * A flag from `/v1/me`, or several of which any one will do. Undefined means
   * everyone in this module sees it.
   */
  capability?: NavCapability | ReadonlyArray<NavCapability>;
  /** Also shown to a read-only user (`isReadOnly`), who holds no write capability. */
  readOnlyToo?: boolean;
  /** A shorter i18n key for the phone's bottom tabs, where five items share 390 px. */
  shortLabelKey?: string;
  /**
   * A count beside the label — what waits for this user there. A hook, called
   * once per rendered item and only while the item is visible; null or 0 shows
   * nothing.
   */
  badge?: () => number | null;
};

type NavCapability =
  | 'canManagePayroll'
  | 'canManageReaders'
  | 'canManageUsers'
  | 'canManageSettings'
  | 'canReadLogs'
  | 'canEditHotelSettings'
  | 'canQualifyHotelNights'
  | 'canEditCashierSettings'
  | 'canSettleCatering'
  | 'canReportCash'
  | 'canAcceptCashReports'
  | 'canKeepMainDesk'
  | 'canPayOut'
  | 'canSeeCashier'
  | 'canSeeCashOverview'
  | 'canSeeReports';

export type ModuleNav = {
  /** Grouped; a divider is drawn between groups. */
  left: ReadonlyArray<ReadonlyArray<NavItem>>;
  /** Pushed to the trailing edge, next to the account controls. */
  right: ReadonlyArray<NavItem>;
  /**
   * Under `sm`, the items leave the top bar for a tab bar at the bottom of the
   * screen, in thumb reach. For a module used standing up, on a phone.
   */
  bottomOnPhone?: boolean;
};

/**
 * One shared width for every item.
 *
 * Sized for the longest Polish label plus its icon, so a
 * short label like `Pulpit` still occupies the same slot and the bar does not
 * reflow when a capability hides an item.
 */
const ITEM_WIDTH = 'w-36';

/** The module's items this user may see, by the rule every nav item follows. */
const useVisibleNav = (nav: ModuleNav) => {
  const capabilities = useCapabilities();
  const isReadOnly = useIsReadOnly();

  const visible = (item: NavItem) => {
    if (item.capability === undefined) return true;
    if (item.readOnlyToo && isReadOnly) return true;
    const required: ReadonlyArray<NavCapability> = typeof item.capability === 'string' ? [item.capability] : item.capability;

    return required.some((capability) => capabilities[capability]);
  };

  return {
    leftGroups: nav.left.map((group) => group.filter(visible)).filter((g) => g.length > 0),
    rightGroup: nav.right.filter(visible),
  };
};

export const ModuleNavBar = ({ nav }: { nav: ModuleNav }) => {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { leftGroups, rightGroup } = useVisibleNav(nav);
  const scroller = useRef<HTMLElement>(null);
  const { start, end, update } = useOverflowEdges(scroller);

  // The current item scrolled into view: on a phone it may sit past the edge,
  // and a highlighted item nobody can see says nothing. Only the bar scrolls —
  // `scrollIntoView` would move the page too.
  useLayoutEffect(() => {
    const bar = scroller.current;
    const current = bar?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!bar || !current) return;
    const left = current.offsetLeft;
    const right = left + current.offsetWidth;
    if (left < bar.scrollLeft || right > bar.scrollLeft + bar.clientWidth) {
      bar.scrollLeft = Math.max(0, left - (bar.clientWidth - current.offsetWidth) / 2);
    }
    update();
  }, [pathname, update]);

  const scrollBy = (direction: -1 | 1) => {
    const bar = scroller.current;
    if (!bar) return;
    const delta = direction * Math.max(bar.clientWidth * 0.7, 120);
    if (typeof bar.scrollBy === 'function') bar.scrollBy({ left: delta, behavior: 'smooth' });
    else bar.scrollLeft += delta;
  };

  return (
    /*
      The wrapper holds the hints; the nav inside scrolls. It stays as the
      header's spacer when the items move to the bottom tabs on a phone.
    */
    <div className="relative flex min-w-0 flex-1 self-stretch">
      {/*
        Horizontal scroll rather than wrap: nine equal-width items already fill a
        laptop bar, and wrapping would steal a second row from every screen that
        measures "what's left under the chrome".
      */}
      <nav
        ref={scroller}
        aria-label={t('nav.label')}
        className={cn(
          'relative flex min-w-0 flex-1 items-center gap-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
          nav.bottomOnPhone && 'max-sm:hidden',
        )}
      >
        {/*
          `shrink-0`, not `min-w-0`: every item is fixed-width and will not shrink,
          so a shrinkable wrapper only lets the items overflow it — underneath the
          trailing group, which then takes their clicks. Unshrinkable, the overflow
          lands on the nav itself, and the nav scrolls.
        */}
        <div className="flex shrink-0 items-center">
          {leftGroups.map((group, groupIndex) => (
            <ul
              key={group.map((item) => item.to).join('-')}
              className={cn(
                'flex items-center gap-0.5',
                groupIndex > 0 ? 'ml-2 border-l border-border-default pl-2' : null,
              )}
            >
              {group.map((item) => (
                <NavLink key={item.to} item={item} pathname={pathname} />
              ))}
            </ul>
          ))}
        </div>

        {rightGroup.length > 0 ? (
          <ul className="ml-auto flex shrink-0 items-center gap-0.5 border-l border-border-default pl-2">
            {rightGroup.map((item) => (
              <NavLink key={item.to} item={item} pathname={pathname} />
            ))}
          </ul>
        ) : null}
      </nav>

      {/*
        The hint that the bar goes on: a fade and an arrow on the side that has
        more. For the pointer only — hidden from assistive technology and out of
        the tab order, since every item is a link Tab already reaches, and focus
        scrolls it into view.
      */}
      <OverflowHint side="start" show={start} onClick={() => scrollBy(-1)} hidden={nav.bottomOnPhone} />
      <OverflowHint side="end" show={end} onClick={() => scrollBy(1)} hidden={nav.bottomOnPhone} />
    </div>
  );
};

/**
 * Whether a horizontal scroller has more past its start and its end edge,
 * kept current on scroll and on resize.
 */
const useOverflowEdges = (ref: RefObject<HTMLElement | null>) => {
  const [edges, setEdges] = useState({ start: false, end: false });

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const start = el.scrollLeft > 1;
    const end = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setEdges((now) => (now.start === start && now.end === end ? now : { start, end }));
  }, [ref]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    update();
    el.addEventListener('scroll', update, { passive: true });
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    observer?.observe(el);
    if (el.firstElementChild) observer?.observe(el.firstElementChild);

    return () => {
      el.removeEventListener('scroll', update);
      observer?.disconnect();
    };
  }, [ref, update]);

  return { ...edges, update };
};

const OverflowHint = ({
  side,
  show,
  onClick,
  hidden,
}: {
  side: 'start' | 'end';
  show: boolean;
  onClick: () => void;
  hidden?: boolean;
}) => {
  const Icon = side === 'start' ? ChevronLeft : ChevronRight;

  return (
    <button
      type="button"
      aria-hidden
      tabIndex={-1}
      data-overflow={side}
      onClick={onClick}
      className={cn(
        'absolute inset-y-0 z-10 flex w-9 items-center text-fg-muted transition-opacity hover:text-fg',
        side === 'start'
          ? 'left-0 justify-start bg-linear-to-r from-bg-raised from-40% to-transparent'
          : 'right-0 justify-end bg-linear-to-l from-bg-raised from-40% to-transparent',
        show ? 'opacity-100' : 'pointer-events-none opacity-0',
        hidden && 'max-sm:hidden',
      )}
    >
      <Icon className="size-4" />
    </button>
  );
};

/** The item's count, from its own hook; nothing when there is none. */
const NavBadge = ({ useCount, className }: { useCount: () => number | null; className?: string }) => {
  const { t } = useTranslation();
  const count = useCount();
  if (!count) return null;

  return (
    <>
      <span
        aria-hidden
        className={cn(
          'inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-accent px-1.5 text-[11px] font-semibold text-accent-fg tabular-nums',
          className,
        )}
      >
        {count > 99 ? '99+' : count}
      </span>
      <span className="sr-only">{t('navBar.waiting', { count })}</span>
    </>
  );
};

const NavLink = ({ item, pathname }: { item: NavItem; pathname: string }) => {
  const { t } = useTranslation();
  const active = isActive(pathname, item.to);
  const label = t(item.labelKey);

  return (
    <li className="shrink-0">
      <Link
        // The nav is data, so these are strings the router cannot narrow to its
        // route union. The routes are declared literally in `router.tsx`, which
        // is what keeps typed routing working for every hand-written `<Link>`.
        to={item.to as never}
        params={item.params as never}
        title={label}
        // ⚠️ Both of these exist to stop the router marking a SECOND item.
        //
        // `Link` sets `aria-current="page"` and `data-status="active"` itself,
        // by prefix, so on `/hr/timesheet` it also marks the module root `/hr`
        // — two highlighted items, and the dashboard permanently lit. Our own
        // `isActive` is the rule that knows a module root must match exactly.
        //
        // `activeOptions.exact` narrows the router's own matching to agree, and
        // `activeProps`/`inactiveProps` are emptied so nothing is contributed
        // beyond what is set explicitly below. Found by an e2e test asserting
        // exactly one current item.
        activeOptions={{ exact: true }}
        activeProps={{}}
        inactiveProps={{}}
        className={cn(
          // An item with a count may grow past the shared width rather than
          // truncate its label; the others keep the slot.
          item.badge ? 'min-w-36' : ITEM_WIDTH,
          'flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-sm whitespace-nowrap transition-colors',
          active
            ? 'bg-bg-hover font-medium text-fg'
            : 'text-fg-muted hover:bg-bg-hover hover:text-fg',
        )}
        aria-current={active ? 'page' : undefined}
      >
        <item.icon aria-hidden className="size-4 shrink-0" />
        <span className="truncate">{label}</span>
        {item.badge ? <NavBadge useCount={item.badge} /> : null}
      </Link>
    </li>
  );
};

/** Tabs a phone's bottom bar holds before the rest go under "Więcej". */
const BOTTOM_TABS = 5;

/**
 * The module's items as a tab bar on the bottom edge of a phone — icon, short
 * label, count — for a module that sets `bottomOnPhone`. From `sm` up it is
 * not drawn and the top bar has the items.
 *
 * Fixed over the bottom of the scrolling `main`; its height, safe area
 * included, is published as `--bottom-nav-height` on the document root, which
 * `main` pads by and a screen's sticky action bar sits above. Zero while it is
 * hidden. More than five items: four, then "Więcej" with the rest in a sheet,
 * which slides up from the bottom edge (`Modal` with `sheet`).
 */
export const ModuleBottomBar = ({ nav }: { nav: ModuleNav }) => {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { leftGroups, rightGroup } = useVisibleNav(nav);
  const items = [...leftGroups.flat(), ...rightGroup];
  const tabs = items.length > BOTTOM_TABS ? items.slice(0, BOTTOM_TABS - 1) : items;
  const rest = items.length > BOTTOM_TABS ? items.slice(BOTTOM_TABS - 1) : [];
  const [moreOpen, setMoreOpen] = useState(false);
  const bar = useRef<HTMLElement>(null);

  useBottomNavHeight(bar, items.length > 0);

  if (items.length === 0) return null;

  const moreActive = rest.some((item) => isActive(pathname, item.to));

  return (
    <>
      <nav
        ref={bar}
        aria-label={t('nav.label')}
        data-bottom-nav
        className="fixed inset-x-0 bottom-0 z-30 border-t border-border-default bg-bg-raised pb-[env(safe-area-inset-bottom)] shadow-[0_-4px_12px_-8px_rgb(0_0_0/0.2)] sm:hidden"
      >
        <ul className="flex">
          {tabs.map((item) => (
            <li key={item.to} className="min-w-0 flex-1">
              <BottomTab item={item} active={isActive(pathname, item.to)} />
            </li>
          ))}
          {rest.length > 0 ? (
            <li className="min-w-0 flex-1">
              <button
                type="button"
                aria-haspopup="dialog"
                aria-expanded={moreOpen}
                onClick={() => setMoreOpen(true)}
                className={cn(tabClass(moreActive), 'w-full')}
              >
                <Ellipsis aria-hidden className="size-5" />
                <span className="line-clamp-2">{t('navBar.more')}</span>
              </button>
            </li>
          ) : null}
        </ul>
      </nav>
      {moreOpen ? (
        <Modal title={t('navBar.more')} onClose={() => setMoreOpen(false)} sheet>
          <ul className="-mx-2 -my-1">
            {rest.map((item) => {
              const active = isActive(pathname, item.to);
              const label = t(item.labelKey);

              return (
                <li key={item.to}>
                  <Link
                    to={item.to as never}
                    params={item.params as never}
                    activeOptions={{ exact: true }}
                    activeProps={{}}
                    inactiveProps={{}}
                    aria-current={active ? 'page' : undefined}
                    onClick={() => setMoreOpen(false)}
                    className={cn(
                      'flex min-h-12 items-center gap-3 rounded-md px-2 text-sm',
                      active ? 'bg-bg-hover font-medium text-fg' : 'text-fg hover:bg-bg-hover',
                    )}
                  >
                    <item.icon aria-hidden className="size-5 shrink-0 text-fg-muted" />
                    <span className="min-w-0 flex-1 truncate">{label}</span>
                    {item.badge ? <NavBadge useCount={item.badge} /> : null}
                  </Link>
                </li>
              );
            })}
          </ul>
        </Modal>
      ) : null}
    </>
  );
};

const tabClass = (active: boolean) =>
  cn(
    'relative flex min-h-14 flex-col items-center justify-center gap-0.5 px-1 pt-1.5 pb-1 text-center text-[11px] leading-tight transition-colors',
    'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring',
    active ? 'font-semibold text-accent' : 'text-fg-muted hover:text-fg',
  );

const BottomTab = ({ item, active }: { item: NavItem; active: boolean }) => {
  const { t } = useTranslation();
  const label = t(item.shortLabelKey ?? item.labelKey);

  return (
    <Link
      to={item.to as never}
      params={item.params as never}
      // See `NavLink`: the router must not mark a second item.
      activeOptions={{ exact: true }}
      activeProps={{}}
      inactiveProps={{}}
      aria-current={active ? 'page' : undefined}
      className={tabClass(active)}
    >
      {/* Always there and faded in or out, so the mark moves to the new tab rather than blinking. */}
      <span
        aria-hidden
        className={cn(
          'absolute inset-x-3 top-0 h-0.5 rounded-full bg-accent motion-safe:transition-opacity motion-safe:duration-200',
          !active && 'opacity-0',
        )}
      />
      <span className="relative">
        <item.icon aria-hidden className="size-5" />
        {item.badge ? <NavBadge useCount={item.badge} className="absolute -top-1.5 left-3" /> : null}
      </span>
      <span className="line-clamp-2 max-w-full break-words">{label}</span>
    </Link>
  );
};

/**
 * Publishes the bar's height as `--bottom-nav-height` on the document root
 * while it is drawn — 0 from `sm` up, where it is `display: none` — and
 * removes it when the bar goes.
 */
const useBottomNavHeight = (ref: RefObject<HTMLElement | null>, present: boolean) => {
  useLayoutEffect(() => {
    const root = document.documentElement;
    const el = ref.current;
    if (!present || !el) return;
    const set = () => root.style.setProperty('--bottom-nav-height', `${el.offsetHeight}px`);
    set();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(set);
    observer?.observe(el);
    window.addEventListener('resize', set);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', set);
      root.style.removeProperty('--bottom-nav-height');
    };
  }, [ref, present]);
};

/**
 * Whether a nav item covers the current location.
 *
 * Not equality: `/hr/employees/454` is still the employees section, and the
 * reference item points at `/hr/reference/departments` while its sibling tabs
 * live under `/hr/reference/`. Matching on the item's own path as a prefix keeps
 * one item highlighted instead of none.
 *
 * ⚠️ **Two segments now, not one.** This compared `/${to.split('/')[1]}` while
 * every path was `/timesheet`. Under `/hr/timesheet` that first segment is `hr`
 * for every item, so every item would light up at once. The module root
 * (`/hr`) is therefore matched exactly — otherwise it prefixes every screen in
 * its own module and the dashboard never goes dark.
 */
export const isActive = (pathname: string, to: string): boolean => {
  const section = to.split('/').slice(0, 3).join('/');

  // A module root — `/hr`, `/hotel` — has no third segment, so a prefix match
  // would claim every screen beneath it.
  if (section.split('/').length < 3) {
    return pathname === section;
  }

  return pathname === section || pathname.startsWith(`${section}/`);
};
